/**
 * CONTRACT.md §2.5 — type and text, plus §2.10a's three ranks of number.
 *
 * The P1 rank is `46 → 28 → 20`: one `HeroFigure` per page, one `SubFigure`
 * under it, and `StatValue` (in `Stat.tsx`) for everything else that is a
 * number. "One per page" is a page test, not a runtime check —
 * `expectOneHero(container)` in `src/test/contract.ts`.
 *
 * **A page never formats.** `Num`, `HeroFigure`, `SubFigure` and `DateText`
 * take typed values and render them through `useFormat()`, which is the SPA's
 * one formatter (`.docs/00-index.md` §5; `Intl` and `toLocaleString` are
 * lint-banned everywhere else). Given a string instead, they render it as
 * written — that is for the figures the mockups compose out of two numbers
 * (`7 / 6`, `4 of 5`) and for a time that is already a label (`08:00`).
 */

import type { DateStyle, Money } from '@fareast/shared'
import type { ReactNode } from 'react'

import { useFormat } from '../../format/useFormat.js'
import { asChildElement, cx, type Common } from './common.js'
import { externalLinkProps } from './links.js'

/** What a figure may be given: an amount, a count, or a phrase already written. */
export type FigureValue = Money | number | string

function isMoney(value: FigureValue): value is Money {
  return typeof value === 'object' && 'amount' in value && 'currency' in value
}

/** One formatter for all four figure components, so they cannot disagree. */
function useFigureText(): (value: FigureValue) => string {
  const format = useFormat()
  return (value) => {
    if (typeof value === 'string') return value
    return isMoney(value) ? format.money(value) : format.number(value)
  }
}

/* ------------------------------------------------------------------- figures */

export type HeroFigureProps = Common & {
  readonly value: FigureValue
  /** The `%` in `studio/clients.html` — `.hero-figure sup`. */
  readonly sup?: string | undefined
  /** `.num--bad` — past due, failed. The caption must still say the word. */
  readonly bad?: boolean | undefined
}

/** `--fs-7`. **One per page.** */
export function HeroFigure({ value, sup, bad, ...common }: HeroFigureProps) {
  const text = useFigureText()
  return (
    <div className={cx('hero-figure', 'num', bad && 'num--bad')} {...common}>
      {text(value)}
      {sup === undefined ? null : <sup>{sup}</sup>}
    </div>
  )
}

export type SubFigureProps = Common & {
  readonly value: FigureValue
  readonly bad?: boolean | undefined
}

/** `--fs-6`. The second number on the page, and only that. */
export function SubFigure({ value, bad, ...common }: SubFigureProps) {
  const text = useFigureText()
  return (
    <div className={cx('sub-figure', 'num', bad && 'num--bad')} {...common}>
      {text(value)}
    </div>
  )
}

export type HeroCapProps = Common & { readonly children: ReactNode }

/** The plain-sans muted caption naming a figure — or a small block (§5, `.block-cap`). */
export function HeroCap({ children, ...common }: HeroCapProps) {
  return (
    <div className="hero-cap" {...common}>
      {children}
    </div>
  )
}

/* --------------------------------------------------------------------- prose */

export type AnswerProps = Common & { readonly children: ReactNode }

/** P1 — the `--fs-4` sentence a panel opens with. One per panel. */
export function Answer({ children, ...common }: AnswerProps) {
  return (
    <p className="answer" {...common}>
      {children}
    </p>
  )
}

export type ProseProps = Common & { readonly children: ReactNode }

export function Prose({ children, ...common }: ProseProps) {
  return (
    <p className="prose" {...common}>
      {children}
    </p>
  )
}

export type SerifQuoteProps = Common & { readonly children: ReactNode }

/** The editorial pull-quote, metered to 58ch. At most one per page. */
export function SerifQuote({ children, ...common }: SerifQuoteProps) {
  return (
    <p className="serif-quote" {...common}>
      {children}
    </p>
  )
}

/* ------------------------------------------------------------- inline pieces */

export type NumProps = Common & {
  /** An amount or a count, formatted here. Leave it out and `children` is rendered. */
  readonly value?: Money | number | undefined
  /** `.num--lg` — `--fs-4`. */
  readonly lg?: boolean | undefined
  /** `.num--bad` — the figure itself is broken. */
  readonly bad?: boolean | undefined
  /** `.strike` — the list price a discount replaced. */
  readonly strike?: boolean | undefined
  readonly children?: ReactNode
}

/** Mono, tabular numerals. Every money and count figure in a sentence or a cell. */
export function Num({ value, lg, bad, strike, children, ...common }: NumProps) {
  const text = useFigureText()
  return (
    <span className={cx('num', lg && 'num--lg', bad && 'num--bad', strike && 'strike')} {...common}>
      {value === undefined ? children : text(value)}
    </span>
  )
}

export type MonoProps = Common & { readonly children: ReactNode }

/** Mono without tabular numerals — a token, not a column of figures. */
export function Mono({ children, ...common }: MonoProps) {
  return (
    <span className="mono" {...common}>
      {children}
    </span>
  )
}

export type DateTextProps = Common & {
  readonly value: Date | string
  /** `short` (`6 Aug 2026`) by default. */
  readonly style?: DateStyle | undefined
  /** Overrides the viewer's zone — `client/calls.html` shows one client's own. */
  readonly zone?: string | undefined
  /** `.nowrap` — outside a table cell, where the `.table td .date` rule does it. */
  readonly nowrap?: boolean | undefined
}

/** A date in a data row. Mono `--fs-2`; `TimelineItem`'s date is plain sans instead. */
export function DateText({ value, style, zone, nowrap, ...common }: DateTextProps) {
  const format = useFormat()
  return (
    <span className={cx('date', nowrap && 'nowrap')} {...common}>
      {format.date(value, style ?? 'short', zone)}
    </span>
  )
}

export type DayTextProps = Common & {
  /** A `DateOnly` — `2026-08-26`. An instant is `DateText`'s. */
  readonly value: string
  /** `short` (`26 Aug 2026`) by default; `weekday` gives `Wed 26 Aug`. */
  readonly style?: DateStyle | undefined
  readonly nowrap?: boolean | undefined
}

/**
 * A **day** in a data row, as opposed to an instant.
 *
 * `DateText` renders in the viewer's zone, which is right for a call and wrong for
 * an invoice's due date: `2026-08-26` read in New York is the 25th. This reads it at
 * noon UTC, which no zone can carry into another day. Sessions 19's first three
 * pages each wrote `<DateText value={`${day}T12:00:00.000Z`} zone="UTC" />` before
 * this existed, which is three chances to leave the `zone` off.
 *
 * Same `.date` class as `DateText`, because it is the same kind of thing on the
 * page; what differs is which clock it is read on.
 */
export function DayText({ value, style, nowrap, ...common }: DayTextProps) {
  const format = useFormat()
  return (
    <span className={cx('date', nowrap && 'nowrap')} {...common}>
      {format.day(value, style)}
    </span>
  )
}

export type RefProps = Common & {
  /** `FES-2026-047`, `WO-2026-014`, `REQ-119`, `TSK-1042`. */
  readonly value: string
  /** `.b` — the contract's own `<span class="ref b">` for the ref a panel is about. */
  readonly strong?: boolean | undefined
  /** `.nowrap` — outside a table cell, where the `.table td .ref` rule does it. */
  readonly nowrap?: boolean | undefined
}

export function Ref({ value, strong, nowrap, ...common }: RefProps) {
  return (
    <span className={cx('ref', strong && 'b', nowrap && 'nowrap')} {...common}>
      {value}
    </span>
  )
}

export type BProps = Common & { readonly children: ReactNode }

/**
 * `.b` — weight 600 on something that is not a heading. §2.5's table lists it;
 * the mockups use it seventeen times for the first line of a `NeedsItem` or a
 * `FileRow`, where `<b>` would be the wrong element because the emphasis is
 * rank rather than importance.
 */
export function B({ children, ...common }: BProps) {
  return (
    <span className="b" {...common}>
      {children}
    </span>
  )
}

/* ----------------------------------------------------- the one-word modifiers */

export type TextProps = Common & { readonly children: ReactNode }

export function Muted({ children, ...common }: TextProps) {
  return (
    <span className="muted" {...common}>
      {children}
    </span>
  )
}

export function Dim({ children, ...common }: TextProps) {
  return (
    <span className="dim" {...common}>
      {children}
    </span>
  )
}

export function Small({ children, ...common }: TextProps) {
  return (
    <span className="small" {...common}>
      {children}
    </span>
  )
}

/**
 * The smallest text, inline — or as a **line of its own**.
 *
 * The mockups use `.xs` both ways and the difference is the element: `<span class="xs">`
 * inside a sentence (`studio/analytics.html`), and `<div class="xs muted">` as the
 * second line under a name (`studio/clients.html`'s "Since 6 Jan 2026 · 7 blocks",
 * `studio/money.html`'s reference under a client). One class, two elements, and the
 * class does not say which — so this is a prop rather than a second primitive.
 *
 * It is not cosmetic. Rendered as a span, "Vela Interiors" and "FES-2026-049" are two
 * inline boxes with no whitespace between them and print as `Vela InteriorsFES-2026-049`,
 * which is what the first studio screenshots showed on three pages.
 */
export function Xs({ children, block, ...common }: TextProps & { readonly block?: boolean }) {
  if (block === true) {
    return (
      <div className="xs" {...common}>
        {children}
      </div>
    )
  }
  return (
    <span className="xs" {...common}>
      {children}
    </span>
  )
}

/** Either an `href`, or one element child to hand the class to. Never both. */
type LinkTarget =
  | { readonly href: string; readonly asChild?: false | undefined }
  | { readonly href?: undefined; readonly asChild: true }

export type LinkishProps = Common & {
  /** `.b` — `<a class="b linkish">`, the one link in a row that is the row's subject. */
  readonly strong?: boolean | undefined
  readonly children: ReactNode
} & LinkTarget

/**
 * An underlined inline link. Ink, not blue.
 *
 * A destination off this origin opens in its own tab — `links.ts` decides, because
 * whether `meet_url` is somebody else's document is a fact about the URL and not
 * one the calls page should have to state. `asChild` hands the class to a router
 * `Link`, which is internal by construction and asks nothing.
 */
export function Linkish({ href, asChild, strong, children, ...common }: LinkishProps) {
  const className = cx('linkish', strong && 'b') ?? 'linkish'
  if (asChild === true) return asChildElement(children, className, common)
  return (
    <a className={className} href={href} {...externalLinkProps(href)} {...common}>
      {children}
    </a>
  )
}

export type MoreProps = Common & { readonly children: ReactNode } & LinkTarget

/** The muted P3 "and the rest →" link. */
export function More({ href, asChild, children, ...common }: MoreProps) {
  if (asChild === true) return asChildElement(children, 'more', common)
  return (
    <a className="more" href={href} {...externalLinkProps(href)} {...common}>
      {children}
    </a>
  )
}
